import { Widget, WidgetEditor } from 'jamespot-front-business';
import { jCommentList, WidgetContext, WidgetDisplayMode, WidgetWrapperProps } from 'jamespot-user-api';
import React, { ReactNode, useCallback, useEffect } from 'react';
import { shallowEqual } from 'react-redux';
import { useComponentVisible } from './hooks';
import { DisplayWidget } from './Widget';

// eslint-disable-next-line import/no-cycle
import { WidgetLayer } from './WidgetLayer/WidgetLayer';
import { WidgetState } from './WidgetState';
import { WidgetWrapperCoreEditHover } from './WidgetWrapperCoreEditHover';

import { useAppDispatch, useAppSelector } from '../../redux/store';
import { WidgetStyleWrapper } from './wrapper/components/WidgetStyleWrapper';
import { JRCWidgetIconWrapper } from './wrapper/JRCWidgetIconWrapper';
type WidgetWrapperCoreProps = {
    uniqid: string;
    widgetObject?: Partial<jCommentList>;
    widget: WidgetWrapperProps;
    inplace?: boolean;
    mode: WidgetDisplayMode;
    width?: number | undefined;
    context: WidgetContext;
    onWidgetUpdate?: () => void;
    cannotDisplayComponent?: (widget: string) => ReactNode;
    dataCy?: string;
};

/*
    This is a widget wrapper
*/
export const WidgetWrapperCore = ({
    uniqid,
    widgetObject,
    widget,
    inplace = true,
    mode,
    width,
    context,
    onWidgetUpdate,
    cannotDisplayComponent,
    dataCy,
}: WidgetWrapperCoreProps) => {
    const dispatch = useAppDispatch();
    const widgetWrapper = useAppSelector((state) => Widget.selectors.selectWidget(state, uniqid));
    const indexOrder = useAppSelector((state) => state.widgets.editableMap[uniqid], shallowEqual);
    const currentEditableWidgetId = useAppSelector((state) => state.widgets.currentEditableWidgetId, shallowEqual);
    const isActive = useAppSelector((state) => state.widgets.currentEditableWidgetId === uniqid, shallowEqual);
    const { isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    useEffect(() => {
        if (widgetObject) {
            dispatch(Widget.slice.actions.registerWidgetObject({ uniqid, object: widgetObject }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (widget) {
            /*
                Register widget
            */
            dispatch(Widget.slice.actions.registerWidget({ uniqid, widget, context }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isComponentVisible) {
            if (uniqid === currentEditableWidgetId) {
                dispatch(Widget.slice.actions.setEditableWidgetId({ uniqid: undefined }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentVisible]);

    useEffect(() => {
        if (isComponentVisible !== isActive) {
            setIsComponentVisible(isActive);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    const registerEditor = useCallback(() => {
        if (widgetWrapper === undefined || mode === 'view') {
            return;
        }
        dispatch(WidgetEditor.slice.actions.flushAllEditor());
        /*
            Register editor
        */
        const { widget } = widgetWrapper;
        dispatch(
            WidgetEditor.slice.actions.registerEditor({
                uniqid: widget.uniqid,
                name: widget.name,
            }),
        );
    }, [dispatch, mode, widgetWrapper]);

    if (widgetWrapper === undefined) {
        return <>This widget cannot be displayed</>;
    }

    if (inplace && mode !== 'view') {
        return (
            <WidgetStyleWrapper
                uniqid={uniqid}
                onClick={() => {
                    setIsComponentVisible(true);
                    if (indexOrder) {
                        dispatch(Widget.slice.actions.setEditableIndex({ index: indexOrder.index }));
                        dispatch(Widget.slice.actions.setEditableWidgetId({ uniqid }));
                    }
                }}>
                <DisplayWidget
                    dataCy={dataCy}
                    width={width}
                    widget={{
                        ...widgetWrapper.widget,
                        ...(widgetWrapper.title && {
                            title: widgetWrapper.title,
                        }),
                    }}
                    inplace={inplace}
                    mode={mode}
                    levelFallback={(level, url) => {
                        return (
                            level <= 2 && (
                                <JRCWidgetIconWrapper
                                    uniqid={widget.widget.uniqid}
                                    name={widget.widget.name}
                                    level={level}
                                    url={url}
                                />
                            )
                        );
                    }}
                />
                <WidgetLayer uniqid={uniqid} mode={mode} />
                <WidgetState name={widgetWrapper.widget.name} uniqid={widgetWrapper.widget.uniqid} inplace={inplace} />
            </WidgetStyleWrapper>
        );
    }

    return (
        <WidgetStyleWrapper uniqid={uniqid} onClick={registerEditor}>
            <DisplayWidget
                dataCy={dataCy}
                widget={{
                    ...widgetWrapper.widget,
                    ...(widgetWrapper.title && {
                        title: widgetWrapper.title,
                    }),
                }}
                inplace={inplace}
                mode={mode}
                width={width}
                onWidgetUpdate={onWidgetUpdate}
                cannotDisplayComponent={cannotDisplayComponent}
                levelFallback={(level, url) => {
                    return (
                        level <= 2 && (
                            <JRCWidgetIconWrapper
                                uniqid={widget.widget.uniqid}
                                name={widget.widget.name}
                                level={level}
                                url={url}
                            />
                        )
                    );
                }}
            />
            <WidgetLayer uniqid={uniqid} mode={mode} />
            {mode === 'edit' && <WidgetWrapperCoreEditHover uniqid={uniqid} name={widget.widget.name} />}
            <WidgetState name={widgetWrapper.widget.name} uniqid={widgetWrapper.widget.uniqid} inplace={inplace} />
        </WidgetStyleWrapper>
    );
};

export default WidgetWrapperCore;
